import type { Plugin, SegmentEvent } from '@segment/analytics-next'

import {
  getGuideTourRuntimeConfig,
  getGuideTourTargetInteractionMapper,
} from '@/guide-tour/config'
import { GUIDE_TOUR_TARGET_INTERACTION_ACTION } from '@/guide-tour/notifications/types'
import { getGuideTourTrackingSnapshot } from '@/guide-tour/tracking'
import type { GuideTourTrackingSnapshot } from '@/guide-tour/tracking'

type TrackPayload = {
  event: string
  properties?: Record<string, unknown>
}

export const interceptGuideTourTargetInteractionEvent = (
  trackEvent: TrackPayload,
  context: GuideTourTrackingSnapshot | null,
  clickedEventName: string,
  viewedEventName: string,
  mapClickedProps = getGuideTourTargetInteractionMapper()
): TrackPayload => {
  const isForeignEvent =
    trackEvent.event !== clickedEventName &&
    trackEvent.event !== viewedEventName

  if (!context?.step?.allowTargetInteraction || !isForeignEvent) {
    return trackEvent
  }

  if (!mapClickedProps) {
    return trackEvent
  }

  const properties = mapClickedProps(context, {
    ...trackEvent.properties,
    action: GUIDE_TOUR_TARGET_INTERACTION_ACTION,
  })

  if (!properties) {
    return trackEvent
  }

  return {
    event: clickedEventName,
    properties,
  }
}

const toTrackPayload = (event: SegmentEvent): TrackPayload | null => {
  if (event.type !== 'track' || !event.event) {
    return null
  }

  return {
    event: event.event,
    properties: event.properties ?? {},
  }
}

export const guideTourTargetInteractionInterceptionPlugin = (): Plugin => ({
  name: 'Guide Tour Target Interaction Interceptor',
  type: 'before',
  version: '1.0.0',
  isLoaded: () => true,
  load: () => Promise.resolve(),
  track: ctx => {
    const analytics = getGuideTourRuntimeConfig()?.analytics

    const clickedEventName = analytics?.clickedEventName
    const viewedEventName = analytics?.viewedEventName

    if (!clickedEventName || !viewedEventName) {
      return ctx
    }
    const payload = toTrackPayload(ctx.event)

    if (!payload) {
      return ctx
    }

    const nextPayload = interceptGuideTourTargetInteractionEvent(
      payload,
      getGuideTourTrackingSnapshot(),
      clickedEventName,
      viewedEventName
    )

    ctx.event = {
      ...ctx.event,
      event: nextPayload.event,
      properties: nextPayload.properties,
    }

    return ctx
  },
})
